// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { pdf } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseTable, ConfirmModal, Loading, UnderDevelopment } from '~/components/common';
import { FeesOrderDetail, InformationOrderComponent, OrderPdf, TimeLineActionOrder } from '~/components/module/order';
import {
  EventDetailsQuoteDetail,
  EventTineLineQuoteDetail,
  FormPaymentQuote,
  NoteQuoteDetail,
  PaymentListModal,
  TotalInfoQuoteDetail,
} from '~/components/module/quotes';
import { PickSlipPdf } from '~/components/module/job';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IBodyUpdate, IHeaderCellType } from '~/utils/interface/common';
import { getOrderDetail, sendMailOrder } from '~/thunks/order/orderThunk';
import { formatCurrencyUSD, handleCalculateDiscountedPrice } from '~/utils/helper/common';
import {
  DEFAULT_INVOICE_FILE_NAME_PDF,
  DEFAULT_ORDER_FILE_NAME_PDF,
  EMPTY_STRING,
  EMPTY_VALUE,
} from '~/utils/constants/common';
import {
  IOrderDetail,
  IOrderItem,
  IOrderItemAddOn,
  IOrderPdf,
  IParamQueryOrderDetail,
  IProductOrderItem,
  ISubRentalOrderDetail,
} from '~/utils/interface/order';
import {
  TypeInventoryGroupDay,
  TypeListInventory,
  BusinessTypeEnum,
  TypeQuoteDetailEnum,
  DiscountTypeEnum,
} from '~/utils/enum/common';
import { IPaymentQuote } from '~/utils/interface/quotes';
import { createPayment } from '~/thunks/payment/paymentThunk';
import { orderActions, selectIsRefreshOrderDetail } from '~/thunks/order/orderSlice';
// Styles, images, icons
import styles from './InfoOrderTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IProductOrderItem>[] => {
  return [
    {
      id: 'name',
      label: t('order_detail_table_name'),
      render: (record: IProductOrderItem) => (
        <Stack gap={2} direction={'row'} alignItems={'center'}>
          {record?.parentType && (
            <Typography className={cx('addOn')}>
              {record?.type === TypeListInventory.PRODUCT && record?.parentType === TypeListInventory.PACKAGE
                ? t('order_detail_table_package_item')
                : t('order_detail_table_add_on')}
            </Typography>
          )}

          <Stack gap={'2px'}>
            <Stack flexDirection={'row'} gap={2} alignItems={'center'}>
              <Box>{record?.name || EMPTY_VALUE}</Box>

              {record.type === TypeListInventory.PACKAGE && (
                <Box className={cx('viewBox')}>{t('form_orders_label_package')}</Box>
              )}

              {record?.isDelete && (
                <Box className={cx('delete')}>{t('common_button_delete', { ns: 'common/base' })}</Box>
              )}
            </Stack>

            {record?.variantName && <Box className={cx('description')}>{record?.variantName}</Box>}

            {record?.description && <Box className={cx('description')}>{record?.description}</Box>}

            {/* {record?.isRequired && (
              <Box className={cx('textRequired')}>{t('common_text_required', { ns: 'common/base' })}</Box>
            )} */}
          </Stack>
        </Stack>
      ),
      width: 450,
    },
    {
      id: 'discountRate',
      label: t('order_detail_table_discount'),
      render: (record: IProductOrderItem) =>
        record?.discountType === DiscountTypeEnum.PERCENTAGE
          ? `${Number(record?.discountRate)}%`
          : formatCurrencyUSD({ value: Number(record?.discountRate) }) || EMPTY_VALUE,
    },
    {
      id: 'price',
      label: t('order_detail_table_rental_price'),
      render: (record: IProductOrderItem) => {
        return (
          <Stack direction={'row'}>
            <Typography className={cx('price', record?.discountRate && 'viewPrice')}>
              {formatCurrencyUSD({ value: record?.price, showDecimal: true })}
            </Typography>

            {!!record?.discountRate && (
              <>
                <Typography className={cx('price')}>/</Typography>
                <Typography className={cx('price')}>
                  {formatCurrencyUSD({
                    value: handleCalculateDiscountedPrice(
                      record?.price,
                      Number(record?.discountRate),
                      record?.discountType
                    ),
                    showDecimal: true,
                  })}
                </Typography>
              </>
            )}
          </Stack>
        );
      },
    },
    {
      id: 'laborCost',
      label: t('order_detail_table_labor_cost'),
      render: (record: IProductOrderItem) => formatCurrencyUSD({ value: record?.laborCost }) || EMPTY_VALUE,
    },
    {
      id: 'quantity',
      label: t('order_detail_table_quantity'),
      render: (record: IProductOrderItem) => record?.quantity || EMPTY_VALUE,
    },
    {
      id: 'total',
      label: t('order_detail_table_total'),
      render: (record: IProductOrderItem) =>
        record?.isRequired && record?.parentType === TypeListInventory.PACKAGE
          ? EMPTY_STRING
          : formatCurrencyUSD({ value: record?.total, showDecimal: true }),
    },
  ];
};

const InfoOrderTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const dispatch = useAppDispatch();
  const { id: orderId } = useParams();
  const { t } = useTranslation(['admin/order', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  const isRefreshOrderDetail = useSelector(selectIsRefreshOrderDetail);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [detailOrder, setDetailOrder] = useState<IOrderDetail>();

  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  const [isShowAllPaymentModal, setIsShowAllPaymentModal] = useState<boolean>(false);
  const [isShowPaymentModal, setIsShowPaymentModal] = useState<boolean>(false);
  const [isShowConfirmSendEmailModal, setIsShowConfirmSendEmailModal] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IParamQueryOrderDetail = {
      orderId: orderId || EMPTY_STRING,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    handleGetOrderDetail(params);
  }, []);

  useEffect(() => {
    if (!isRefreshOrderDetail) return;

    const params: IParamQueryOrderDetail = {
      orderId: orderId || EMPTY_STRING,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    handleGetOrderDetail(params);

    dispatch(orderActions.setRefreshOrderDetail(false));
  }, [isRefreshOrderDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetOrderDetail = (params: IParamQueryOrderDetail) => {
    setIsLoading(true);

    dispatch(getOrderDetail(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        setDetailOrder(data as IOrderDetail);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDataTableOrderProduct = (
    orderItems: IOrderItem[],
    orderItemAddOns: IOrderItemAddOn[],
    orderSubRentals: ISubRentalOrderDetail[] = []
  ) => {
    const orderItemMap = orderItems.reduce<Record<string, IOrderItem>>((acc, item) => {
      acc[item?.id || EMPTY_STRING] = item;
      return acc;
    }, {});

    const subRentalMap = orderSubRentals.reduce<Record<string, ISubRentalOrderDetail>>((acc, item) => {
      acc[item.inventoryId] = item;
      return acc;
    }, {});

    const addOnMap = orderItemAddOns.reduce<Record<string, IProductOrderItem[]>>((acc, addOn) => {
      const parent = orderItemMap[addOn?.parentId || EMPTY_STRING];
      const parentType = parent ? parent.type : EMPTY_STRING;

      const subRental = subRentalMap[addOn.inventoryId];

      const displayName = subRental
        ? `${addOn.name} / ${t('form_orders_title_sub_rental')} / ${subRental.quantity} - ${formatCurrencyUSD({
            value: subRental.price,
            showDecimal: true,
          })}`
        : addOn.name;

      if (!acc[addOn?.parentId || EMPTY_STRING]) acc[addOn?.parentId || EMPTY_STRING] = [];
      acc[addOn?.parentId || EMPTY_STRING].push({
        ...addOn,
        parentType,
        name: displayName,
        isOverbooking: isItemOverbooking(addOn),
      } as IProductOrderItem);

      return acc;
    }, {});

    const result: IProductOrderItem[] = [];

    for (const item of orderItems) {
      const subRental = subRentalMap[item.inventoryId];

      result.push({
        ...item,
        name: subRental
          ? `${item.name} / ${t('form_orders_title_sub_rental')} / ${subRental.quantity} - ${formatCurrencyUSD({
              value: subRental.price,
              showDecimal: true,
            })}`
          : item.name,
        isOverbooking: isItemOverbooking(item),
      } as IProductOrderItem);

      const addOns = addOnMap[item?.id || EMPTY_STRING];
      if (addOns && addOns.length > 0) {
        result.push(...addOns);
      }
    }

    return result;
  };

  const isItemOverbooking = (item: { isOverbooking?: boolean; overbookedQuantity?: number } | undefined): boolean => {
    return Boolean(item?.isOverbooking) || Number(item?.overbookedQuantity ?? 0) > 0;
  };

  const handleSavePayment = async (body: IPaymentQuote) => {
    setIsLoading(true);

    const convertData: IPaymentQuote = {
      ...body,
      businessId: orderId,
      businessType: BusinessTypeEnum.ORDER,
    };

    dispatch(createPayment(convertData))
      .unwrap()
      .then((_res) => {
        dispatch(orderActions.setRefreshOrderDetail(true));
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
        handleClosePaymentModal();
      });
  };

  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
  };

  const handleProceedToSignature = async () => {
    if (!detailOrder || !orderId) return;

    const data: IOrderPdf = {
      client: detailOrder?.client,
      orderNumber: detailOrder?.orderNumber || EMPTY_STRING,
      poNumber: detailOrder?.poNumber || EMPTY_STRING,
      startDate: detailOrder?.startDate || EMPTY_STRING,
      startTime: detailOrder?.startTime || EMPTY_STRING,
      endDate: detailOrder?.endDate || EMPTY_STRING,
      endTime: detailOrder?.endTime || EMPTY_STRING,
      orderItems: detailOrder?.orderItems || [],
      orderAddOns: detailOrder?.orderAddOns || [],
      deliveryCost: detailOrder?.deliveryCost || EMPTY_STRING,
      damageWaiver: detailOrder?.damageWaiver || EMPTY_STRING,
      laborFee: detailOrder?.laborFee || EMPTY_STRING,
      discountRate: detailOrder?.discountRate || EMPTY_STRING,
      depositMadeAmount: detailOrder?.depositMadeAmount || EMPTY_STRING,
      couponAmount: detailOrder?.couponAmount || EMPTY_STRING,
      customFees: detailOrder?.orderCustom,
      productSubtotal: detailOrder?.productSubtotal || EMPTY_STRING,
      total: detailOrder?.total || EMPTY_STRING,
      taxAmount: detailOrder?.taxAmount || EMPTY_STRING,
      mileage: detailOrder?.mileage || EMPTY_STRING,
      externalNote: detailOrder?.externalNote || EMPTY_STRING,
      internalNote: detailOrder?.internalNote || EMPTY_STRING,
      dataProduct:
        handleDataTableOrderProduct(
          detailOrder?.orderItems || [],
          detailOrder?.orderAddOns || [],
          detailOrder?.orderSubRentals || []
        ) || [],
    };

    const pdfBlob = await generatePdfBlob(data);

    const formData = new FormData();

    if (pdfBlob) {
      formData.append('boldSignFile', pdfBlob, DEFAULT_ORDER_FILE_NAME_PDF);
      formData.append('invoicePdf', pdfBlob, DEFAULT_INVOICE_FILE_NAME_PDF);
    }

    setIsLoading(true);

    const payload: IBodyUpdate<FormData> = {
      id: orderId,
      body: formData,
    };

    dispatch(sendMailOrder(payload))
      .unwrap()
      .then((_res) => {})
      .catch(() => {})
      .finally(() => {
        handleShowConfirmSendEmailModal();
        setIsLoading(false);
      });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handleShowAllPaymentModal = () => {
    setIsShowAllPaymentModal(true);
  };

  const handleCloseAllPaymentModal = () => {
    setIsShowAllPaymentModal(false);
  };

  const handleShowPaymentModal = () => {
    setIsShowPaymentModal(true);
  };

  const generatePdfBlob = async (data: IOrderPdf): Promise<Blob | null> => {
    try {
      return await pdf(<OrderPdf orderPdf={data} />).toBlob();
    } catch (error) {
      console.error('Generate PDF failed: ', error);
      return null;
    }
  };

  const handleShowConfirmSendEmailModal = () => {
    setIsShowConfirmSendEmailModal(!isShowConfirmSendEmailModal);
  };

  const handlePrintOrder = async () => {
    if (!detailOrder || !orderId) return;

    const data: IOrderPdf = {
      client: detailOrder?.client,
      orderNumber: detailOrder?.orderNumber || EMPTY_STRING,
      poNumber: detailOrder?.poNumber || EMPTY_STRING,
      startDate: detailOrder?.startDate || EMPTY_STRING,
      startTime: detailOrder?.startTime || EMPTY_STRING,
      endDate: detailOrder?.endDate || EMPTY_STRING,
      endTime: detailOrder?.endTime || EMPTY_STRING,
      orderItems: detailOrder?.orderItems || [],
      orderAddOns: detailOrder?.orderAddOns || [],
      deliveryCost: detailOrder?.deliveryCost || EMPTY_STRING,
      damageWaiver: detailOrder?.damageWaiver || EMPTY_STRING,
      laborFee: detailOrder?.laborFee || EMPTY_STRING,
      discountRate: detailOrder?.discountRate || EMPTY_STRING,
      depositMadeAmount: detailOrder?.depositMadeAmount || EMPTY_STRING,
      couponAmount: detailOrder?.couponAmount || EMPTY_STRING,
      customFees: detailOrder?.orderCustom,
      productSubtotal: detailOrder?.productSubtotal || EMPTY_STRING,
      total: detailOrder?.total || EMPTY_STRING,
      taxAmount: detailOrder?.taxAmount || EMPTY_STRING,
      mileage: detailOrder?.mileage || EMPTY_STRING,
      externalNote: detailOrder?.externalNote || EMPTY_STRING,
      internalNote: detailOrder?.internalNote || EMPTY_STRING,
      dataProduct:
        handleDataTableOrderProduct(
          detailOrder?.orderItems || [],
          detailOrder?.orderAddOns || [],
          detailOrder?.orderSubRentals || []
        ) || [],
    };

    const pdfBlob = await generatePdfBlob(data);

    if (pdfBlob instanceof Blob) {
      const url = URL.createObjectURL(pdfBlob);
      const newTab = window.open(url, '_blank', 'noopener,noreferrer');
      if (newTab) newTab.opener = null;

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  };

  const handlePrintPickSlip = async () => {
    if (!detailOrder || !orderId) return;
    const data = {
      client: detailOrder?.client,
      startDate: detailOrder?.startDate || EMPTY_STRING,
      startTime: detailOrder?.startTime || EMPTY_STRING,
      endDate: detailOrder?.endDate || EMPTY_STRING,
      endTime: detailOrder?.endTime || EMPTY_STRING,
      jobItems:
        handleConvertDataProductPickSlip(
          detailOrder?.orderItems || [],
          detailOrder?.orderAddOns || [],
          detailOrder?.orderSubRentals || []
        ) || [],
      total: detailOrder?.total || EMPTY_STRING,
      orderId: detailOrder?.id || EMPTY_STRING,
      internalNote: detailOrder?.internalNote || EMPTY_STRING,
      externalNote: detailOrder?.externalNote || EMPTY_STRING,
    };
    const pdfBlob = await generatePickSlipPdfBlob(data);

    if (pdfBlob instanceof Blob) {
      const url = URL.createObjectURL(pdfBlob);
      const newTab = window.open(url, '_blank', 'noopener,noreferrer');
      if (newTab) newTab.opener = null;

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    }
  };

  const handleConvertDataProductPickSlip = (
    orderItems: IOrderItem[] = [],
    orderItemAddOns: IOrderItemAddOn[] = [],
    orderSubRentals: ISubRentalOrderDetail[] = []
  ) => {
    const subRentalMap = orderSubRentals.reduce<Record<string, ISubRentalOrderDetail>>((acc, sub) => {
      acc[String(sub.inventoryId)] = sub;
      return acc;
    }, {});

    const applySubRentalToItem = (item: any): any => {
      const subRental = subRentalMap[String(item.inventoryId)];

      const updatedName = subRental
        ? `${item.name} / ${t('form_orders_title_sub_rental')} / ${subRental.quantity} - ${formatCurrencyUSD({
            value: subRental.price,
            showDecimal: true,
          })}`
        : item.name;

      return {
        ...item,
        name: updatedName,
        jobAddOns: Array.isArray(item.jobAddOns) ? item.jobAddOns.flat().map(applySubRentalToItem) : [],
      };
    };

    const addOnMap = orderItemAddOns.reduce<Record<string, any[]>>((acc, addOn) => {
      const parentId = String(addOn?.parentId || EMPTY_STRING);

      if (!acc[parentId]) {
        acc[parentId] = [];
      }

      acc[parentId].push({
        ...addOn,
        jobAddOns: [],
      });

      return acc;
    }, {});

    const itemsWithAddOns = orderItems.map((item) => ({
      ...item,
      jobAddOns: addOnMap[String(item?.id || EMPTY_STRING)] || [],
    }));

    return itemsWithAddOns.map(applySubRentalToItem);
  };

  const generatePickSlipPdfBlob = async (data: any): Promise<Blob | null> => {
    try {
      return await pdf(<PickSlipPdf dataPdf={data} />).toBlob();
    } catch (error) {
      console.error('Generate PDF failed: ', error);
      return null;
    }
  };
  //#endregion Handle Function

  return (
    <Stack id='infoOrderTabComponent' className={cx('container')} gap={4}>
      <InformationOrderComponent detailOrder={detailOrder} />

      <Grid2 spacing={4} container>
        <Grid2 direction={'column'} size={3}>
          <TimeLineActionOrder
            onPrintPickSlip={handlePrintPickSlip}
            onPrintOrder={handlePrintOrder}
            onSendEmail={handleShowConfirmSendEmailModal}
            onMakePayment={handleShowPaymentModal}
            detailOrder={detailOrder}
            handleShowAllPaymentModal={handleShowAllPaymentModal}
          />
        </Grid2>
        <Grid2 direction={'column'} size={9}>
          <Stack mb={4} className={cx('orderProduct')}>
            <Typography className={cx('title')}>{t('order_detail_order_details')}</Typography>

            <BaseTable
              dataSource={
                handleDataTableOrderProduct(
                  detailOrder?.orderItems || [],
                  detailOrder?.orderAddOns || [],
                  detailOrder?.orderSubRentals || []
                ) || []
              }
              columns={columns(t)}
            />
          </Stack>

          <EventDetailsQuoteDetail orderDetail={detailOrder} />

          <EventTineLineQuoteDetail orderDetail={detailOrder} />

          <NoteQuoteDetail orderDetail={detailOrder} />

          <FeesOrderDetail detailOrder={detailOrder} />

          <TotalInfoQuoteDetail detailOrder={detailOrder} />
        </Grid2>
      </Grid2>

      {isLoading && <Loading />}

      {isShowAllPaymentModal && (
        <PaymentListModal
          orderDetail={detailOrder}
          onClose={handleCloseAllPaymentModal}
          type={TypeInventoryGroupDay.ORDER}
        />
      )}

      {isShowConfirmSendEmailModal && (
        <ConfirmModal
          type='default'
          icon={icons.commonIconEmail}
          title={t('order_detail_send_mail', { ns: 'admin/order' })}
          onClose={handleShowConfirmSendEmailModal}
          onConfirm={handleProceedToSignature}
        />
      )}

      {isShowPaymentModal && detailOrder && (
        <FormPaymentQuote quoteDetail={detailOrder} onClose={handleClosePaymentModal} onSubmit={handleSavePayment} />
      )}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Stack>
  );
};

export default InfoOrderTab;
